import React from 'react';
import { normalizeSiteContentLineBreaks } from './siteContentText';

/**
 * Utility functions for formatting text content from database
 */

export const formatTextContent = (text: string): React.ReactNode => {
  if (!text) return null;

  // Clean up the text and preserve intentional line breaks
  let cleanText = normalizeSiteContentLineBreaks(text).trim();

  // Split on double line breaks to preserve paragraph structure
  const paragraphs = cleanText
    .split(/\n\s*\n/) // Split on double line breaks (paragraph breaks)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, index) => {
        // Handle single line breaks within paragraphs as <br> tags
        const lines = paragraph.split('\n').filter(line => line.trim());

        return (
          <p key={index} className="leading-relaxed">
            {lines.map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {line.trim()}
                {lineIndex < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export const formatCompactText = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Clean up the text
  let cleanText = normalizeSiteContentLineBreaks(text).trim();
  
  // For compact text, preserve line breaks but keep it more condensed
  const lines = cleanText.split('\n').filter(line => line.trim());
  
  if (lines.length === 1) {
    return <span className="leading-relaxed">{lines[0]}</span>;
  }
  
  return (
    <div className="space-y-1">
      {lines.map((line, index) => (
        <div key={index} className="leading-relaxed">
          {line.trim()}
        </div>
      ))}
    </div>
  );
};

// New function for preserving exact formatting as entered in admin
export const formatExactText = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Preserve the exact formatting as entered, just clean up escape sequences
  const cleanText = normalizeSiteContentLineBreaks(text);

  return (
    <div className="whitespace-pre-line leading-relaxed text-white !text-white !opacity-100">
      {cleanText}
    </div>
  );
};